// src/pages/accesoModulos.js
//
// CANDADO DE RUTA del ERP (09/10/2026). El menú lateral ya escondía los módulos que no están en Permisos, pero
// cualquiera con sesión abría su pantalla escribiendo la dirección (por ejemplo /reporte-ganancias). Layout.js usa
// estas dos funciones para NO montar esa pantalla y mostrar "No tenés acceso a este módulo".
//
// Es la capa de la PANTALLA: los datos los siguen cuidando las reglas de Firestore y las Cloud Functions de cada
// módulo (SEGURIDAD_GUIA.md §5, bitácora del 09/10/2026 en PLAN_SEGURIDAD.md). Pruebas: accesoModulos.test.js.
//
// 🔴 Quién puede abrir un módulo lo decide la MISMA lista que dibuja el menú lateral (Layout.js le pasa
// `visibleEnMenu`): acá no hay una segunda copia de la regla de Permisos que se pueda desincronizar.

import { matchRoutes } from 'react-router-dom';
import { MENU_SECTIONS_COMPLETO } from './menuConfig';

// Una ruta por módulo del menú. Se reconoce con matchRoutes, el MISMO comparador que usan las <Routes> de App.js:
// así /Reporte-Ganancias, /reporte-ganancias/ o /reporte%2Dganancias, que React Router abre igual, no se escapan.
const RUTAS_DE_MODULOS = MENU_SECTIONS_COMPLETO.flatMap((seccion) =>
  seccion.items.map((item) => ({ path: item.path, moduloId: item.id }))
);

// Módulos que Layout le ofrece a TODOS fuera del menú: el botón "Agregá tus accesos" de la barra de arriba lleva a
// Accesos Rápidos, y esa pantalla solo lista los módulos de la propia persona. Medido el 09/10/2026: 20 de 23
// colaboradores no lo tienen en Permisos y 6 de ellos ya armaron su barra por ahí.
export const MODULOS_ABIERTOS_FUERA_DEL_MENU = ['accesos-rapidos'];

// Módulos del menú que ve TODO el mundo sin tenerlos en Permisos (cada quien ve lo suyo): el Manual de Uso, Mi
// Asistencia, Mis Cursos (15/10/2026: cada colaborador toma ahí sus cursos y baja sus certificados) y Mi Desempeño
// (23/10/2026, Evaluación del Desempeño tanda 6: su scorecard, sus objetivos y lo que dijo su jefe). Layout.js los
// usa en el menú lateral y en Accesos Rápidos; por eso tampoco van en la lista de GestionPermisos.js.
export const MODULOS_PARA_TODOS = ['manual-uso', 'mi-asistencia', 'mis-cursos', 'mi-desempeno'];

/** El id del módulo del menú que abre esta dirección, o null si no es de uno (Inicio, rutas internas o de detalle). */
export function moduloDeLaRuta(pathname) {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return null;
  const coincidencias = matchRoutes(RUTAS_DE_MODULOS, pathname);
  return coincidencias && coincidencias.length > 0 ? coincidencias[coincidencias.length - 1].route.moduloId : null;
}

/**
 * Qué hace el candado con la pantalla pedida:
 *  'abrir'      → se monta como siempre (también lo que no es un módulo del menú);
 *  'esperar'    → los permisos todavía se están leyendo: ni el módulo ni el aviso, solo "Cargando módulo...";
 *  'sin-acceso' → "No tenés acceso a este módulo" (el módulo no se monta ni se descarga su código).
 *
 * @param {string|null} p.moduloId        lo que devuelve moduloDeLaRuta
 * @param {'todos'|string[]|null} p.permisosUsuario  el de Layout (null = cargando)
 * @param {boolean} p.esAdmin             rol admin: abre todo, igual que en el menú, sin esperar la lectura
 * @param {(id: string) => boolean} p.visibleEnMenu  ¿el menú lateral de esta persona muestra ese módulo?
 */
export function estadoDelCandado({ moduloId, permisosUsuario, esAdmin, visibleEnMenu }) {
  if (!moduloId) return 'abrir';
  if (esAdmin) return 'abrir';
  if (MODULOS_ABIERTOS_FUERA_DEL_MENU.includes(moduloId)) return 'abrir';
  if (permisosUsuario === null || permisosUsuario === undefined) return 'esperar';
  return visibleEnMenu(moduloId) ? 'abrir' : 'sin-acceso';
}
